import { setWindowLocation } from 'js-utils/http';
import { AU_LOCALE } from 'js-utils/intl';
import {
    trackEventFromState,
    trackEventFromStateOnPageUnload,
} from '@eb/site-analytics';
import { logCheckpointOnPageChange } from '@eb/janus';
import {
    BASE_URL,
    CHANGE_PLAN_DOWNGRADE_PATH,
    CHANGE_PLAN_NON_ADMIN_PATH,
    CHANGE_PLAN_PATH,
    CHANGE_PLAN_SALES_PATH,
    CHANGE_PLAN_UPDATE_PATH,
    CONTACT_SALES_URL,
    CREATE_PATH,
    PACKAGE_ONE,
    PACKAGE_THREE,
    PACKAGE_TWO,
    SIGNIN_PATH,
    SIGNIN_URL,
} from '../constants/main';
import { setPackageOne, setPackageTwo } from './onboarding';
import {
    GA_CHOOSE_PACKAGE,
    GA_PACKAGE_SELECTED,
    NAVIGATE_TO_ESSENTIALS_CHECKPOINT,
    NAVIGATE_TO_PREMIUM_CHECKPOINT,
    NAVIGATE_TO_PROFESSIONAL_CHECKPOINT,
    ORG_MARKETING_CATEGORY,
    OVERVIEW_CTA_ACTION,
} from '../constants/analytics';
import {
    canUpdateEssentialsV2,
    trackAnalyticsEventAU,
} from '../containers/utils';

export const redirectToSignIn = (referrer) => (dispatch, getState) => {
    const state = getState();

    trackEventFromStateOnPageUnload(state, {
        action: OVERVIEW_CTA_ACTION,
        category: ORG_MARKETING_CATEGORY,
    });

    setWindowLocation(`${SIGNIN_PATH}${referrer && `/?referrer=${referrer}`}`);
};

export const redirectToCreate = () => (dispatch, getState) => {
    const state = getState();
    const {
        user: { userId },
    } = state;

    trackEventFromStateOnPageUnload(state, {
        action: OVERVIEW_CTA_ACTION,
        category: ORG_MARKETING_CATEGORY,
        userId,
    });

    setWindowLocation(CREATE_PATH);
};

export const redirectToContactSales = () =>
    setWindowLocation(CONTACT_SALES_URL);

const redirectToEssentialsSignIn = () =>
    setWindowLocation(`${SIGNIN_URL}&pckg=essentials`);

const redirectToProfessionalSignIn = () =>
    setWindowLocation(`${SIGNIN_URL}&pckg=professional`);

const redirectToSalesDowngrade = () =>
    setWindowLocation(`${BASE_URL}${CHANGE_PLAN_SALES_PATH}`);

const redirectToDowngrade = () =>
    setWindowLocation(`${BASE_URL}${CHANGE_PLAN_DOWNGRADE_PATH}`);

const redirectToUpdate = () =>
    setWindowLocation(`${BASE_URL}${CHANGE_PLAN_UPDATE_PATH}`);

const redirectToNonAdminChangePlan = () =>
    setWindowLocation(`${BASE_URL}${CHANGE_PLAN_NON_ADMIN_PATH}`);

export const redirectToChangePlan = (gaAction, label) => (_, getState) => {
    const state = getState();

    if (gaAction) {
        trackEventFromState(state, {
            action: gaAction,
            category: ORG_MARKETING_CATEGORY,
            label,
        });
    }

    setWindowLocation(`${BASE_URL}${CHANGE_PLAN_PATH}`);
};

export const redirectToEssentials = (gaAction, label) => (
    dispatch,
    getState,
) => {
    const state = getState();
    const {
        user: { isAuthenticated, canAccessLegacyConvert, userPlan },
        assortments: { assortmentsAvailability },
        env: { locale },
        localeDefaultPlan,
    } = state;
    let dispatchAction = null;

    if (gaAction) {
        trackEventFromState(state, {
            action: gaAction,
            category: ORG_MARKETING_CATEGORY,
            label,
        });
    }

    if (isAuthenticated) {
        if (canUpdateEssentialsV2({ locale, assortmentsAvailability })) {
            dispatchAction = redirectToUpdate;
        } else if (userPlan === PACKAGE_ONE) {
            return;
        } else if (canAccessLegacyConvert) {
            dispatchAction = redirectToNonAdminChangePlan;
        } else if (userPlan === PACKAGE_TWO) {
            dispatchAction = redirectToDowngrade;
        } else if (userPlan === PACKAGE_THREE) {
            dispatchAction = redirectToSalesDowngrade;
        } else {
            dispatchAction = setPackageOne();
        }
    } else {
        dispatchAction = redirectToEssentialsSignIn;
        if (locale === AU_LOCALE) {
            trackAnalyticsEventAU({
                action: GA_PACKAGE_SELECTED,
                category: GA_CHOOSE_PACKAGE,
                dimensions: {
                    userId: null,
                    userPlan: null,
                    version: null,
                    locale,
                },
            });
        }
    }

    if (dispatchAction) {
        logCheckpointOnPageChange(
            NAVIGATE_TO_ESSENTIALS_CHECKPOINT,
            state.env.domain,
        );
        dispatch(dispatchAction);
    }
};

export const redirectToProfessional = (gaAction, label) => (
    dispatch,
    getState,
) => {
    const state = getState();
    const {
        user: { isAuthenticated, canAccessLegacyConvert, userPlan },
        env: { locale },
        localeDefaultPlan,
    } = state;
    let dispatchAction = null;

    if (gaAction) {
        trackEventFromState(state, {
            action: gaAction,
            category: ORG_MARKETING_CATEGORY,
            label,
        });
    }

    if (isAuthenticated) {
        if (userPlan === PACKAGE_TWO) {
            return;
        }

        if (canAccessLegacyConvert) {
            dispatchAction = redirectToNonAdminChangePlan;
        } else if (userPlan === PACKAGE_THREE) {
            dispatchAction = redirectToSalesDowngrade;
        } else {
            dispatchAction = setPackageTwo();
        }
    } else {
        dispatchAction = redirectToProfessionalSignIn;
        if (locale === AU_LOCALE) {
            trackAnalyticsEventAU({
                action: GA_PACKAGE_SELECTED,
                category: GA_CHOOSE_PACKAGE,
                dimensions: {
                    userId: null,
                    userPlan: null,
                    version: null,
                    locale,
                },
            });
        }
    }

    if (dispatchAction) {
        logCheckpointOnPageChange(
            NAVIGATE_TO_PROFESSIONAL_CHECKPOINT,
            state.env.domain,
        );
        dispatch(dispatchAction);
    }
};

export const redirectToPremium = (gaAction, label) => (dispatch, getState) => {
    const state = getState();
    const {
        user: { userPlan },
    } = state;

    if (gaAction) {
        trackEventFromState(state, {
            action: gaAction,
            category: ORG_MARKETING_CATEGORY,
            label,
        });
    }

    if (userPlan !== PACKAGE_THREE) {
        logCheckpointOnPageChange(
            NAVIGATE_TO_PREMIUM_CHECKPOINT,
            state.env.domain,
        );
        dispatch(redirectToContactSales);
    }
};
